import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const Templates = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', body_text: '' });
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [wabaId, setWabaId] = useState(null);
  const [viewMode, setViewMode] = useState('library'); // 'library' or 'synced'
  const [testDialog, setTestDialog] = useState(false);
  const [testTemplate, setTestTemplate] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [templateParams, setTemplateParams] = useState({});
  const [syncingTemplates, setSyncingTemplates] = useState(new Set());

  useEffect(() => {
    fetchTemplates();
    fetchLibraryTemplates();
    fetchWhatsAppIntegration();
  }, []);

  const fetchWhatsAppIntegration = async () => {
    try {
      const response = await api.get('/lms/integrations/');
      const whatsappIntegration = response.data.find(
        integration => integration.integration_type === 'whatsapp' && integration.is_active
      );
      if (whatsappIntegration) {
        // WABA ID is stored in connected_resource_id
        setWabaId(whatsappIntegration.connected_resource_id);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp integration:', error);
    }
  };

  const handleCreateTemplate = () => {
    // Facebook WhatsApp Manager URL
    // If we have WABA ID, use it, otherwise use general URL
    let url = 'https://business.facebook.com/latest/whatsapp_manager/message_templates';
    if (wabaId) {
      url += `?asset_id=${wabaId}`;
    }
    // Open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/lms/whatsapp/templates/');
      setTemplates(response.data.templates || []);
      setLastSyncedAt(response.data.last_synced_at);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchLibraryTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/lms/whatsapp/template-library/');
      setLibraryTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching library templates:', error);
      setError('Failed to load template library');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      const response = await api.post('/lms/whatsapp/templates/refresh/');
      
      // Show success message
      setSuccess(`Successfully synced ${response.data.total_count} templates from WhatsApp`);
      
      // Refresh templates list
      await fetchTemplates();
    } catch (error) {
      console.error('Error syncing templates:', error);
      setError('Failed to sync templates from WhatsApp');
    } finally {
      setSyncing(false);
    }
  };

  const filteredTemplates = (viewMode === 'library' ? libraryTemplates : templates).filter(template =>
    template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.body_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatSyncTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setPreviewDialog(true);
  };

  const handleEdit = async (template) => {
    try {
      // Fetch full template details including components
      const response = await api.get(`/lms/whatsapp/templates/${template.id}/`);
      const fullTemplate = response.data;
      
      setEditingTemplate(fullTemplate);
      setEditFormData({
        name: fullTemplate.name || '',
        body_text: fullTemplate.body_text || ''
      });
      setEditDialog(true);
    } catch (error) {
      console.error('Error fetching template details:', error);
      setError(error.response?.data?.error || 'Failed to load template details');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;
    
    try {
      setError(null);
      
      // Update the body text in components
      const updatedComponents = [...(editingTemplate.components || [])];
      const bodyComponent = updatedComponents.find(c => c.type === 'BODY');
      
      if (bodyComponent) {
        bodyComponent.text = editFormData.body_text;
      } else if (editFormData.body_text) {
        // Add body component if it doesn't exist
        updatedComponents.push({
          type: 'BODY',
          text: editFormData.body_text
        });
      }
      
      await api.patch(`/lms/whatsapp/templates/${editingTemplate.id}/`, {
        name: editFormData.name,
        components: updatedComponents
      });
      
      // Refresh the templates list to get updated data
      await fetchTemplates();
      
      setEditDialog(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error updating template:', error);
      setError(error.response?.data?.error || 'Failed to update template');
    }
  };


  const handleOpenTestDialog = (template) => {
    setTestTemplate(template);
    setTestPhone('');
    
    // Parse template variables from body_text
    const bodyText = template.body_text || '';
    const variableMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    const uniqueVariables = [...new Set(variableMatches)].sort();
    
    // Initialize template params with empty values
    const initialParams = {};
    uniqueVariables.forEach((varMatch) => {
      const varNumber = varMatch.match(/\d+/)[0];
      initialParams[varNumber] = '';
    });
    
    setTemplateParams(initialParams);
    setTestDialog(true);
  };

  const handleSendTest = async () => {
    if (!testTemplate || !testPhone) {
      setError('Please enter a phone number');
      return;
    }

    // Check if all required template params are filled
    const paramKeys = Object.keys(templateParams);
    const hasEmptyParams = paramKeys.some(key => !templateParams[key] || templateParams[key].trim() === '');
    
    if (paramKeys.length > 0 && hasEmptyParams) {
      setError('Please fill in all template variables');
      return;
    }

    try {
      setSendingTest(true);
      setError(null);
      setSuccess(null);

      // Convert template params object to array
      // The API expects an array of values in order [value1, value2, ...]
      const paramsArray = paramKeys.length > 0 
        ? paramKeys.sort((a, b) => parseInt(a) - parseInt(b)).map(key => templateParams[key])
        : [];

      // Send test message
      const response = await api.post('/lms/whatsapp/messages/send/', {
        to_phone: testPhone,
        message_type: 'template',
        template_name: testTemplate.name,
        language: testTemplate.language || 'en_US',
        template_params: paramsArray
      });

      setSuccess(`Test message sent successfully to ${testPhone}`);
      setTestDialog(false);
      setTestPhone('');
      setTestTemplate(null);
      setTemplateParams({});
    } catch (error) {
      console.error('Error sending test message:', error);
      setError(error.response?.data?.error || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSyncToWhatsApp = async (template) => {
    if (!template.id) {
      setError('Template ID not found');
      return;
    }

    try {
      // Add template to syncing set
      setSyncingTemplates(prev => new Set(prev).add(template.id));
      setError(null);
      setSuccess(null);

      // Sync template to Facebook/WhatsApp
      const response = await api.post(`/lms/whatsapp/template-library/${template.id}/sync-to-facebook/`);

      setSuccess(
        response.data.note 
          ? `${response.data.message} - ${response.data.note}`
          : response.data.message || 'Template created successfully on WhatsApp'
      );

      // Refresh synced templates to show the new template
      await fetchTemplates();
    } catch (error) {
      console.error('Error syncing template to WhatsApp:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create template on WhatsApp';
      setError(errorMsg);
    } finally {
      // Remove template from syncing set
      setSyncingTemplates(prev => {
        const newSet = new Set(prev);
        newSet.delete(template.id);
        return newSet;
      });
    }
  };
  

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Message Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {viewMode === 'library' ? 'Browse template library' : 'Manage your synced WhatsApp message templates'}
          </Typography>
          {lastSyncedAt && viewMode === 'synced' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Last synced: {formatSyncTime(lastSyncedAt)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={handleCreateTemplate}
            sx={{ 
              bgcolor: '#25D366',
              color: 'white',
              '&:hover': { bgcolor: '#20BA5A' }
            }}
          >
            Open WhatsApp Manager
          </Button>
          {viewMode === 'synced' && (
            <Button
              variant="outlined"
              onClick={handleSync}
              disabled={syncing}
              sx={{ 
                borderColor: '#25D366', 
                color: '#25D366',
                '&:hover': { borderColor: '#20BA5A', bgcolor: 'rgba(37, 211, 102, 0.04)' }
              }}
            >
              {syncing ? 'Syncing...' : 'Sync from WhatsApp'}
            </Button>
          )}
        </Box>
      </Box>

      {/* View Mode Tabs */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Button
          onClick={() => setViewMode('library')}
          sx={{
            borderBottom: viewMode === 'library' ? 2 : 0,
            borderColor: '#25D366',
            borderRadius: 0,
            color: viewMode === 'library' ? '#25D366' : 'text.secondary',
            fontWeight: viewMode === 'library' ? 'bold' : 'normal',
            pb: 1,
            '&:hover': { bgcolor: 'transparent' }
          }}
        >
          Template Library ({libraryTemplates.length})
        </Button>
        <Button
          onClick={() => setViewMode('synced')}
          sx={{
            borderBottom: viewMode === 'synced' ? 2 : 0,
            borderColor: '#25D366',
            borderRadius: 0,
            color: viewMode === 'synced' ? '#25D366' : 'text.secondary',
            fontWeight: viewMode === 'synced' ? 'bold' : 'normal',
            pb: 1,
            '&:hover': { bgcolor: 'transparent' }
          }}
        >
          Synced Templates ({templates.length})
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'background.paper' }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : filteredTemplates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {searchQuery 
              ? 'No templates found matching your search.' 
              : viewMode === 'library' 
                ? 'No templates in the library. Please run the management command to populate templates.' 
                : 'No synced templates yet. Create templates in WhatsApp Manager and sync them here.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template, index) => {
            // Check if this template is already synced (for library view)
            // Match by name and language; consider both APPROVED and PENDING as "synced" (exists on WhatsApp)
            const normalizeLanguage = (lang) => {
              const normalized = (lang || 'en').toLowerCase();
              if (normalized.startsWith('en')) return 'en';
              return normalized.split('_')[0];
            };
            const syncedMatch = viewMode === 'library' && templates.find(t =>
              t.name === template.name &&
              normalizeLanguage(t.language) === normalizeLanguage(template.language)
            );
            const isAlreadySynced = !!syncedMatch;
            const syncedStatus = syncedMatch?.status || null; // 'APPROVED' | 'PENDING' | etc.
            
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  ...(isAlreadySynced && {
                    bgcolor: syncedStatus === 'APPROVED' 
                      ? 'rgba(76, 175, 80, 0.04)' 
                      : 'rgba(255, 152, 0, 0.06)',
                    border: syncedStatus === 'APPROVED'
                      ? '1px solid rgba(76, 175, 80, 0.2)'
                      : '1px solid rgba(255, 152, 0, 0.35)'
                  })
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Typography variant="h6" component="div" fontWeight="bold">
                        {template.name || 'Untitled Template'}
                      </Typography>
                      {viewMode === 'synced' && (
                        <Chip
                          label={template.status || 'APPROVED'}
                          size="small"
                          color={template.status === 'APPROVED' ? 'success' : 'warning'}
                        />
                      )}
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2, 
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflow: 'visible'
                      }}
                    >
                      {template.body_text || 'No content'}
                    </Typography>
                    {template.variable_descriptions && template.variable_descriptions.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Variables:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {template.variable_descriptions.map((varItem, idx) => (
                            <Chip 
                              key={idx}
                              label={`${varItem.var}: ${varItem.description}`}
                              size="small" 
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {template.button_config && template.button_config.buttons && template.button_config.buttons.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Buttons:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {template.button_config.buttons.map((button, idx) => (
                            <Chip 
                              key={idx}
                              label={button.text} 
                              size="small" 
                              variant="outlined"
                              color="secondary"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={template.category || 'UTILITY'} size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', pt: 0, flexWrap: 'wrap' }}>
                    {viewMode === 'library' ? (
                      <>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<SendIcon />}
                            onClick={() => handleOpenTestDialog(template)}
                            sx={{ color: '#25D366' }}
                          >
                            Send Test
                          </Button>
                          {!isAlreadySynced && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => handleSyncToWhatsApp(template)}
                              disabled={syncingTemplates.has(template.id)}
                              sx={{ 
                                bgcolor: '#25D366',
                                color: 'white',
                                '&:hover': { bgcolor: '#20BA5A' }
                              }}
                            >
                              {syncingTemplates.has(template.id) ? 'Creating...' : 'Create on WhatsApp'}
                            </Button>
                          )}
                        </Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            px: 1,
                            color: isAlreadySynced 
                              ? (syncedStatus === 'APPROVED' ? 'success.main' : 'warning.main')
                              : 'text.secondary',
                            fontWeight: isAlreadySynced ? 500 : 400
                          }}
                        >
                          {isAlreadySynced 
                            ? (syncedStatus === 'APPROVED' ? '✓ Already synced' : '✓ Pending approval')
                            : 'Template reference'}
                        </Typography>
                      </>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<SendIcon />}
                        onClick={() => handleOpenTestDialog(template)}
                        sx={{ color: '#25D366' }}
                      >
                        Send Test
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {selectedTemplate.name}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 2, 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflow: 'visible'
                }}
              >
                {selectedTemplate.body_text}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={selectedTemplate.status} 
                  size="small" 
                  color={selectedTemplate.status === 'APPROVED' ? 'success' : 'warning'}
                />
                <Chip label={selectedTemplate.category} size="small" />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => {
              setPreviewDialog(false);
              if (selectedTemplate) {
                handleEdit(selectedTemplate);
              }
            }}
          >
            Edit Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => {
          setEditDialog(false);
          setEditingTemplate(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Template</DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Body Text"
                value={editFormData.body_text}
                onChange={(e) => setEditFormData({ ...editFormData, body_text: e.target.value })}
                margin="normal"
                multiline
                rows={6}
                required
              />
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">
                  Note: Changes will be saved locally. They may be overwritten when you sync templates from WhatsApp.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialog(false);
            setEditingTemplate(null);
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit}
            disabled={!editFormData.name || !editFormData.body_text}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Test Dialog */}
      <Dialog
        open={testDialog}
        onClose={() => {
          setTestDialog(false);
          setTestTemplate(null);
          setTestPhone('');
          setTemplateParams({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Test Message</DialogTitle>
        <DialogContent>
          {testTemplate && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Send a test message using template: <strong>{testTemplate.name}</strong>
              </Typography>
              
              <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, overflow: 'visible' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Template Preview:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    wordBreak: 'break-word',
                    overflow: 'visible',
                    maxHeight: 'none'
                  }}
                >
                  {testTemplate.body_text || 'No content'}
                </Typography>
              </Box>

              {/* Template Variable Inputs */}
              {Object.keys(templateParams).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Template Variables:
                  </Typography>
                  {Object.keys(templateParams)
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map((paramKey) => {
                      // Get variable description from template if available
                      const varDescription = testTemplate.variable_descriptions?.find(
                        v => v.var === `{{${paramKey}}}`
                      );
                      
                      return (
                        <TextField
                          key={paramKey}
                          fullWidth
                          label={varDescription ? `{{${paramKey}}} - ${varDescription.description}` : `Variable {{${paramKey}}}`}
                          value={templateParams[paramKey]}
                          onChange={(e) => setTemplateParams({
                            ...templateParams,
                            [paramKey]: e.target.value
                          })}
                          margin="normal"
                          size="small"
                          required
                          placeholder={varDescription?.description || `Enter value for {{${paramKey}}}`}
                        />
                      );
                    })}
                </Box>
              )}

              <TextField
                fullWidth
                label="Recipient Phone Number"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="e.g., +919876543210 or 919876543210"
                margin="normal"
                required
                helperText="Enter phone number with country code (e.g., +919876543210)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SendIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.dark">
                  Note: The template must be APPROVED in WhatsApp Manager to send messages. Test messages will be sent as actual WhatsApp messages.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setTestDialog(false);
              setTestTemplate(null);
              setTestPhone('');
              setTemplateParams({});
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSendTest}
            disabled={!testPhone || sendingTest}
            startIcon={<SendIcon />}
            sx={{ 
              bgcolor: '#25D366',
              color: 'white',
              '&:hover': { bgcolor: '#20BA5A' }
            }}
          >
            {sendingTest ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Templates;
